/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Chaitanya
*/

import React, { useState, useEffect, useRef } from 'react';
import SelectBox from '@components/form/SelectBox';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';
import PropTypes from 'prop-types';
import { getAttibutes, isVn } from '@global/helpers';
import Colors from '@global/colors';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';

const propTypes = {
  editable: PropTypes.bool,
  isRequired: PropTypes.bool,
  multiple: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  customFilter: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  hideLabel: PropTypes.bool,
  labelKey: PropTypes.string,
};

const defaultProps = {
  editable: true,
  isRequired: false,
  multiple: false,
  hasError: false,
  onChange: () => {},
  customFilter: {},
  value: '',
  hideLabel: false,
  labelKey: 'name',
};

const limit = 10;
const defaultPhoto = require('@/assets/img/default/image-default.png');

const ProductSearch = (props) => {
  const {
    editable = true,
    isRequired = false,
    multiple = false,
    hasError = false,
    customFilter = {},
    value = '',
    hideLabel = false,
    labelKey = 'name',
  } = props;

  const [loading, setLoading] = useState({ initial: false, loadMore: false });
  const [options, setOptions] = useState([]);
  const [skip, setSkip] = useState(0);

  const getProducts = useApi(MsCatalog.getProducts);

  const customFilterRef = useRef(customFilter);

  useEffect(() => {
    customFilterRef.current = customFilter;
  }, [customFilter]);

  const onSearch = async (val, newSkip = 0) => {
    setLoading((prev) => ({
      ...prev,
      initial: !newSkip,
      loadMore: !!newSkip,
    }));

    try {
      const filter = {
        ...(isVn()
          ? { is_active_in_b2b_vn: true }
          : { is_active_in_b2b: true }),
        ...customFilterRef.current,
      };

      if (val) {
        filter.name = { $regex: val, $options: 'i' };
      } else {
        delete filter.name;
      }

      const response = await getProducts.request({
        params: {
          filter: filter,
          skip: newSkip,
          limit: limit,
        },
      });

      if (response.status === 200 && response.data.data.length) {
        const result = response.data.data;
        const newOptions = generateData(result);

        setOptions((prevOptions) =>
          newSkip ? prevOptions.concat(newOptions) : newOptions,
        );
      } else if (!newSkip) {
        setOptions([]);
      }

      setLoading((prev) => ({
        ...prev,
        initial: false,
        loadMore: false,
      }));
    } catch (error) {
      setLoading((prev) => ({
        ...prev,
        initial: false,
        loadMore: false,
      }));
    }
  };

  const generateData = (data) => {
    const products = [];
    for (let index = 0; index < data.length; index++) {
      const prod = data[index];
      for (let index2 = 0; index2 < prod.combinations.length; index2++) {
        const prodCombi = prod.combinations[index2];
        const combi = { name: prod.name };
        combi.reference = prodCombi.reference || '';
        combi.id = prod.id;
        combi.image = getImage(prodCombi.images);
        combi.attributes = getAttibutes(prodCombi.attributes);
        combi.product_combination_id = prodCombi._id;
        combi.ref_name = '[' + combi.reference + '] ' + combi.name;
        if (
          isVn() ? prodCombi.is_active_in_b2b_vn : prodCombi.is_active_in_b2b
        ) {
          products.push(combi);
        }
      }
    }
    return products;
  };

  const getImage = (image) => {
    let img = '';
    if (image?.length) {
      for (let index = 0; index < image.length; index++) {
        if (index === 0) {
          img = image[index].url;
        }
        if (image[index].is_cover) {
          img = image[index].url;
        }
      }
    }
    return img;
  };

  const OptionItem = ({ optionItem, index, onSelect }) => (
    <TouchableOpacity
      onPress={() => onSelect(optionItem)}
      key={'list-product-' + index}
      style={styles.productItem}>
      <View style={styles.productContainer}>
        <View style={styles.imageContainer}>
          <Image
            source={{
              uri: `${optionItem.image ? optionItem.image : defaultPhoto}`,
            }}
            style={{ width: 36, height: 36 }}
            width={36}
            height={36}
          />
        </View>
        <View style={{ width: '80%' }}>
          <SociollaText type="medium" style={styles.productName}>
            {optionItem?.reference ? `[${optionItem.reference}]` : '-'}
          </SociollaText>
          <SociollaText
            type="medium"
            style={[
              styles.productChildName,
              optionItem.name ? { paddingBottom: 8 } : {},
            ]}>
            {optionItem.name}
          </SociollaText>
          {optionItem.attributes ? (
            <SociollaText style={styles.combinationName}>
              {optionItem.attributes}
            </SociollaText>
          ) : (
            false
          )}
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <SelectBox
      label="Product"
      options={options}
      value={value}
      hideLabel={hideLabel}
      placeholder={
        loading.initial || loading.loadMore ? 'Loading...' : 'Select Product'
      }
      OptionItemComponent={OptionItem}
      trackBy="product_combination_id"
      formLabelKey={labelKey}
      isRequired={isRequired}
      multiple={multiple}
      editable={editable && !loading.initial}
      hasError={hasError}
      loading={loading.initial}
      loadingCallback={loading.loadMore}
      withCallback
      withLoadMore
      loadMoreEmpty={skip >= options.length}
      searchCallback={(val) => {
        setSkip(0);
        onSearch(val);
      }}
      onLoadMore={(val) => {
        const newSkip = skip + 10;
        setSkip(newSkip);
        onSearch(val, newSkip);
      }}
      onChange={(val) => props.onChange(val)}
      onReady={() => {
        setSkip(0);
        onSearch();
      }}
    />
  );
};

const styles = StyleSheet.create({
  productItem: {
    width: '100%',
    padding: 15,
    borderBottomColor: Colors.grey0,
    borderBottomWidth: 1,
  },
  productContainer: {
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
  },
  imageContainer: {
    width: '20%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  productName: { fontSize: 12, color: Colors.black1, paddingBottom: 8 },
  productChildName: {
    fontSize: 14,
    color: Colors.black1,
    paddingBottom: 0,
  },
  combinationName: { fontSize: 12, color: Colors.black5 },
});

ProductSearch.propTypes = propTypes;
ProductSearch.defaultProps = defaultProps;

export default ProductSearch;
